// Enums matching the API
export enum ReportFormat {
    GroupEventTrend = 0,
    EventGroupTrend = 1,
    EventTrendOverall = 2,
}

export enum TimePeriod {
    None = 0,
    Week = 1,
    Month = 2,
}

// Defines which AccessEvent rows are in scope: the date range and optional event type / group
// filters. This is also all that's needed for the raw (unshaped) CSV export.
export interface AccessEventReportQueryParams {
    startDate: string; // ISO 8601 format, midnight UTC of the first day: "2024-01-01T00:00:00Z"
    // ISO 8601 format, midnight UTC of the *last* day to include, not that day's last instant:
    // "2024-01-31T00:00:00Z". The API treats this as the start of the calendar day and extends
    // it through the end of that same day itself (see AccessEventService.EndOfDayUtc).
    endDate: string;
    groupIds?: string[] | null;
    eventTypeIds?: number[] | null;
}

// Defines how the events matched by an AccessEventReportQueryParams should be bucketed/shaped
// for display. Not used by the raw export.
export interface AccessEventReportShapeParams {
    format: ReportFormat;
    timePeriod: TimePeriod;
}

// Request payload
export interface AccessEventUsageReportRequest {
    query: AccessEventReportQueryParams;
    shape: AccessEventReportShapeParams;
}

// Time period data
export interface TimePeriodDataDto {
    startDate: string;
    endDate: string;
    count: number;
    uniqueUsers: number;
}

// GroupEventTrend format (Groups → Events → Time periods)
export interface EventTrendDataDto {
    eventTypeId: number;
    eventTypeName: string;
    totalCount: number;
    totalUniqueUsers: number;
    periods: TimePeriodDataDto[];
}

export interface GroupEventTrendDto {
    groupId: string;
    groupName: string;
    totalEvents: number;
    uniqueUsers: number;
    eventTypes: EventTrendDataDto[];
}

export interface GroupEventTrendReportDto {
    groups: GroupEventTrendDto[];
}

// EventGroupTrend format (Events → Groups → Time periods)
export interface GroupTrendDataDto {
    groupId: string;
    groupName: string;
    totalCount: number;
    totalUniqueUsers: number;
    periods: TimePeriodDataDto[];
}

export interface EventGroupTrendDto {
    eventTypeId: number;
    eventTypeName: string;
    totalCount: number;
    totalUniqueUsers: number;
    groups: GroupTrendDataDto[];
}

export interface EventGroupTrendReportDto {
    eventTypes: EventGroupTrendDto[];
}

// EventTrendOverall format (Events → Time periods, no groups)
export interface EventOverallTrendDto {
    eventTypeId: number;
    eventTypeName: string;
    totalCount: number;
    totalUniqueUsers: number;
    periods: TimePeriodDataDto[];
}

export interface EventTrendOverallReportDto {
    totalEvents: number;
    uniqueUsers: number;
    eventTypes: EventOverallTrendDto[];
}

// Unified response wrapper
export interface AccessEventUsageReportResponse {
    startDate: string;
    endDate: string;
    groupIds?: string[] | null;
    eventTypeIds?: number[] | null;
    formatId: number;
    formatName: string;
    timePeriodId: number;
    timePeriodName: string;
    reportData: GroupEventTrendReportDto | EventGroupTrendReportDto | EventTrendOverallReportDto;
}
